import { useMemo, useState } from "react";
import { Box, Chip, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import axios from "../../api/axios";
import { useHasPermission } from "../../contexts/auth/auth.hooks";
import { EXTERNAL_SERVICES_ENDPOINT } from "../../consts";
import { ExternalService, ExternalServiceArea, ExternalServiceCoverage } from "../../types/externalServices";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import CustomModal from "../ui/modal/modal.component";
import ExternalServiceForm from "./externalServiceForm.component";

interface ExternalServiceAutocompleteProps {
  value: OptionRecord | null;
  onChange: (externalService: OptionRecord | null) => void;
  // The job's machines (comma-separated ids) and client, so the list can put
  // the services working at the job's address first (see ExternalServiceViewSet).
  forEquipment?: string;
  forClient?: string | number | null;
  disabled?: boolean;
  error?: string | null;
  className?: string;
}

export default function ExternalServiceAutocomplete({
  value,
  onChange,
  forEquipment,
  forClient,
  disabled = false,
  error,
  className,
}: ExternalServiceAutocompleteProps) {
  const { t } = useTranslation();
  const canAdd = useHasPermission("service.add_externalservice");
  const [addOpened, setAddOpened] = useState(false);

  const queryParams = useMemo(
    () => ({ for_equipment: forEquipment || undefined, for_client: forClient ?? undefined }),
    [forEquipment, forClient]
  );
  const apiConfig = useMemo(() => ({ url: EXTERNAL_SERVICES_ENDPOINT, queryParams }), [queryParams]);

  // A saved service comes back without coverage - that is only worked out
  // when the list is asked about the job's place - so it is picked the way
  // the list would show it.
  const handleCreated = async (created?: ExternalService) => {
    setAddOpened(false);
    if (!created) return;
    let picked: OptionRecord = created;
    try {
      const { data } = await axios.get(EXTERNAL_SERVICES_ENDPOINT, {
        params: { ...queryParams, id: created.id },
      });
      const listed = (Array.isArray(data?.results) ? data.results : data)?.[0];
      if (listed) picked = listed;
    } catch {
      // picked without the coverage hint
    }
    onChange(picked);
  };

  return (
    <>
      <CustomAutocomplete
        size="small"
        apiConfig={apiConfig}
        value={value}
        disabled={disabled}
        onChange={(_, newValue) => onChange(newValue ?? null)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        // Heads the list - or sits under "No options" when there is nothing to
        // pick, e.g. before the workspace's first service.
        addNew={canAdd ? { label: t("Add external service"), onClick: () => setAddOpened(true) } : null}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const coverage: ExternalServiceCoverage | null = option.coverage ?? null;
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important" }}
            >
              <Typography variant="body2" fontWeight={500}>
                {option.name}
              </Typography>
              {Array.isArray(option.areas) && option.areas.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {option.areas.map((area: ExternalServiceArea) => area.city).join(", ")}
                </Typography>
              )}
              {coverage?.covers && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                  label={t("Covers this address · {{distance}} km from {{city}}", {
                    distance: coverage.distance_km,
                    city: coverage.city,
                  })}
                />
              )}
            </Box>
          );
        }}
        renderInput={(params) => {
          const coverage: ExternalServiceCoverage | null = value?.coverage ?? null;
          const coverageHint = coverage
            ? coverage.covers
              ? t("Covers this address · {{distance}} km from {{city}}", {
                  distance: coverage.distance_km,
                  city: coverage.city,
                })
              : t("The job's address is outside this service's area")
            : "";
          return (
            <TextField
              {...params}
              required
              label={t("External service")}
              error={Boolean(error)}
              helperText={error || coverageHint}
              className={className}
            />
          );
        }}
      />

      <CustomModal
        opened={addOpened}
        setOpened={setAddOpened}
        title={t("Add external service")}
        width="min(960px, 96vw)"
        content={<ExternalServiceForm onSuccess={(created) => void handleCreated(created)} />}
      />
    </>
  );
}
